import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type TooltipSide = "top" | "right" | "bottom" | "left";
type TooltipState = { text: string; side: TooltipSide; anchor: HTMLElement } | null;

const VALID_SIDES: readonly TooltipSide[] = ["top", "right", "bottom", "left"];
const DEFAULT_SIDE: TooltipSide = "bottom";

function readSide(el: HTMLElement): TooltipSide {
	const raw = el.dataset.tooltipSide;
	return raw && (VALID_SIDES as readonly string[]).includes(raw)
		? (raw as TooltipSide)
		: DEFAULT_SIDE;
}

/** ホバーしてから tooltip を出すまでの既定の遅延 (ミリ秒)。RichTooltip とも共有する。 */
export const TOOLTIP_DEFAULT_DELAY = 1000;

function readDelay(el: HTMLElement): number {
	if (el.dataset.tooltipDelay === undefined) return TOOLTIP_DEFAULT_DELAY;
	const raw = Number(el.dataset.tooltipDelay);
	return Number.isFinite(raw) && raw >= 0 ? raw : TOOLTIP_DEFAULT_DELAY;
}

const overflows = (node: Element) => node.scrollWidth > node.clientWidth;

/** `data-tooltip-if-truncated` の要素は、自身か子孫の文字が省略されているときだけ tooltip を出す。 */
function isSkippedUntruncated(el: HTMLElement): boolean {
	if (el.dataset.tooltipIfTruncated === undefined) return false;
	return !overflows(el) && !Array.from(el.querySelectorAll("*")).some(overflows);
}

/**
 * グローバルなツールチップ層。`title` 属性を持つ要素にホバーすると、
 * ネイティブ tooltip を抑制した上で CSS anchor positioning で配置された
 * カスタム tooltip を表示する。既定では 1000 ミリ秒だけ遅延して表示するが、
 * `data-tooltip-delay` (ミリ秒) を指定した要素ではその時間だけ遅延して表示する。
 * 表示中に文言を付け外しする要素は、ネイティブ tooltip が漏れないよう
 * `title` の代わりに `data-tooltip` を使う。`data-tooltip` はホバー中の変更も表示に反映する。
 * 省略され得る名前の全文を出すだけの要素は `data-tooltip-if-truncated` を付け、省略時だけ出す。
 * パスのように折り返すと読みにくい文言は `data-tooltip-nowrap` を付け、幅を制限せず 1 行で出す。
 * クリック・右クリックすると tooltip を閉じる (右クリックで開くメニューに被せない)。
 */
export function TooltipLayer() {
	const [state, setState] = useState<TooltipState>(null);
	const anchorRef = useRef<HTMLElement | null>(null);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const observerRef = useRef<MutationObserver | null>(null);

	useEffect(() => {
		const clearTimer = () => {
			if (timerRef.current !== null) {
				clearTimeout(timerRef.current);
				timerRef.current = null;
			}
		};

		const restore = () => {
			clearTimer();
			observerRef.current?.disconnect();
			observerRef.current = null;
			const el = anchorRef.current;
			if (el) {
				if (document.contains(el)) {
					const original = el.dataset.izanagiTitle;
					if (original !== undefined) el.setAttribute("title", original);
					delete el.dataset.izanagiTitle;
					el.style.removeProperty("anchor-name");
					if (el.getAttribute("style") === "") el.removeAttribute("style");
				}
				anchorRef.current = null;
			}
			setState(null);
		};

		const handleOver = (e: MouseEvent) => {
			const target = e.target as Element | null;
			if (!target || !(target instanceof Element)) return;
			const el = target.closest<HTMLElement>(
				"[title]:not([title='']), [data-tooltip]:not([data-tooltip=''])",
			);
			if (!el) return;
			if (anchorRef.current === el) return;
			// 別の tooltip 要素に移動した場合は先に復元する。
			if (anchorRef.current) restore();

			const title = el.getAttribute("title");
			const usesDataTooltip = Boolean(el.dataset.tooltip);
			const readText = () => (usesDataTooltip ? el.dataset.tooltip : title);
			if (usesDataTooltip) {
				observerRef.current = new MutationObserver(() => {
					const text = readText();
					setState((prev) => (prev && text ? { ...prev, text } : null));
				});
				observerRef.current.observe(el, { attributeFilter: ["data-tooltip"] });
			} else if (title) {
				// ネイティブ tooltip を抑制するため title は即座に退避する。
				// カスタム tooltip の表示のみ delay 分だけ遅らせる。
				el.dataset.izanagiTitle = title;
				el.removeAttribute("title");
			}
			el.style.setProperty("anchor-name", "--izanagi-tooltip");
			anchorRef.current = el;

			const show = () => {
				const text = readText();
				if (text && !isSkippedUntruncated(el)) setState({ text, side: readSide(el), anchor: el });
			};
			const delay = readDelay(el);
			if (delay > 0) {
				timerRef.current = setTimeout(() => {
					timerRef.current = null;
					show();
				}, delay);
			} else {
				show();
			}
		};

		const handleOut = (e: MouseEvent) => {
			const el = anchorRef.current;
			if (!el) return;
			const next = e.relatedTarget as Node | null;
			if (next && el.contains(next)) return;
			restore();
		};

		const hide = () => {
			clearTimer();
			setState(null);
		};

		document.addEventListener("mouseover", handleOver, true);
		document.addEventListener("mouseout", handleOut, true);
		document.addEventListener("click", hide, true);
		document.addEventListener("contextmenu", hide, true);
		window.addEventListener("scroll", restore, true);
		window.addEventListener("blur", restore);

		return () => {
			document.removeEventListener("mouseover", handleOver, true);
			document.removeEventListener("mouseout", handleOut, true);
			document.removeEventListener("click", hide, true);
			document.removeEventListener("contextmenu", hide, true);
			window.removeEventListener("scroll", restore, true);
			window.removeEventListener("blur", restore);
			restore();
		};
	}, []);

	if (!state) return null;

	return createPortal(
		<div
			className="izanagi-tooltip"
			data-side={state.side}
			data-nowrap={state.anchor.dataset.tooltipNowrap !== undefined ? "true" : undefined}
			data-testid="izanagi-tooltip"
		>
			{state.text}
		</div>,
		// showModal() 中の <dialog> は top layer に描画されるため、body 直下の tooltip は
		// z-index に関わらずモーダルの背後に隠れる。アンカーがモーダル内にある場合は
		// dialog 要素の中へポータルし、同じ top layer 上に表示する。
		state.anchor.closest("dialog") ?? document.body,
	);
}
